'use client'

/**
 * Granular Recipient Section Components
 *
 * Each section has its own Suspense boundary so they can load/fail independently.
 * This allows partial page rendering even when some API calls fail.
 */
import React, { Suspense } from 'react'

import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { Building2, ChevronRight, MapPin } from 'lucide-react'
import Link from 'next/link'

import { Skeleton } from '@/components/common/Skeleton'

import {
  useRecipientSectionQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import type { RecipientProfile, TopCategory } from '@/types/usaspending'

import { QueryErrorBoundary } from '../DataErrorBoundary'
import { DistributionBar } from '../DistributionBar'
import { Currency, DateDisplay, NumberDisplay } from '../Formatters'

// =============================================================================
// Header Section (basic profile info)
// =============================================================================

interface RecipientHeaderSectionProps {
  id: string
}

export function RecipientHeaderSection({ id }: RecipientHeaderSectionProps) {
  return (
    <QueryErrorBoundary title="Failed to load recipient">
      <Suspense fallback={<RecipientHeaderSkeleton />}>
        <RecipientHeaderContent id={id} />
      </Suspense>
    </QueryErrorBoundary>
  )
}

function RecipientHeaderContent({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const { data: recipient } = useSuspenseQuery(queries.profile(id))
  return <RecipientHeader recipient={recipient} />
}

function RecipientHeader({ recipient }: { recipient: RecipientProfile }) {
  const { location } = recipient

  return (
    <header className="w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        {/* Breadcrumbs */}
        <nav className="mb-6 flex items-center gap-2 text-xs font-medium text-primary-500 dark:text-primary-400">
          <Link href="/" className="transition-colors hover:text-accent-500">
            Home
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-primary-300 dark:text-primary-600" />
          <Link
            href="/recipients/search"
            className="transition-colors hover:text-accent-500"
          >
            Recipients
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-primary-300 dark:text-primary-600" />
          <span className="font-semibold text-primary-900 dark:text-primary-100">
            {recipient.name}
          </span>
        </nav>

        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-800">
            <Building2 className="h-7 w-7 text-primary-400 dark:text-primary-500" />
          </div>
          <div className="flex-1">
            <h1 className="mb-2 text-3xl font-bold leading-tight tracking-tight text-primary-900 dark:text-primary-50 md:text-4xl">
              {recipient.name}
            </h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-primary-600 dark:text-primary-400">
              <div className="flex items-center gap-2">
                <span className="text-xs font-normal uppercase tracking-wider text-primary-400 dark:text-primary-500">
                  UEI
                </span>
                <span className="rounded border border-primary-200 bg-primary-100 px-2 py-0.5 font-mono text-primary-800 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-200">
                  {recipient.uei}
                </span>
              </div>
              {recipient.duns && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-normal uppercase tracking-wider text-primary-400 dark:text-primary-500">
                    DUNS
                  </span>
                  <span className="rounded border border-primary-200 bg-primary-100 px-2 py-0.5 font-mono text-primary-800 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-200">
                    {recipient.duns}
                  </span>
                </div>
              )}
              {location && (
                <span className="flex items-center gap-1 text-primary-500 dark:text-primary-400">
                  <MapPin className="h-3.5 w-3.5" />
                  {location.city}, {location.state}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

function RecipientHeaderSkeleton() {
  return (
    <header className="w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        <nav className="mb-6 flex items-center gap-2 text-xs font-medium text-primary-500">
          <span>Home</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span>Recipients</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <Skeleton className="h-4 w-48" />
        </nav>
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-800">
            <Building2 className="h-7 w-7 text-primary-400" />
          </div>
          <div className="flex-1">
            <Skeleton className="mb-2 h-10 w-80" />
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-5 w-36" />
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

// =============================================================================
// Stats Section (KPI cards)
// =============================================================================

interface RecipientStatsSectionProps {
  id: string
}

export function RecipientStatsSection({ id }: RecipientStatsSectionProps) {
  return (
    <QueryErrorBoundary title="Failed to load stats">
      <Suspense fallback={<RecipientStatsSkeleton />}>
        <RecipientStatsContent id={id} />
      </Suspense>
    </QueryErrorBoundary>
  )
}

function RecipientStatsContent({ id }: { id: string }) {
  const { data: dashboard } = useSuspenseRecipientDashboard(id)

  if (dashboard.stats === null) {
    return <DeferredRecipientStats id={id} />
  }

  return <RecipientStatsCards stats={dashboard.stats} />
}

function DeferredRecipientStats({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const { data: stats, isLoading } = useQuery(queries.stats(id))

  if (isLoading) return <RecipientStatsSkeleton />
  if (!stats) return null

  return <RecipientStatsCards stats={stats} />
}

interface RecipientStatsCardsProps {
  stats: {
    totalPrimeAwards: number
    totalObligatedAmount: number
    lastTransactionDate: string | null
  }
}

function RecipientStatsCards({ stats }: RecipientStatsCardsProps) {
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <StatCard
        label="Total Obligated"
        value={<Currency value={stats.totalObligatedAmount} />}
      />
      <StatCard
        label="Prime Awards"
        value={<NumberDisplay value={stats.totalPrimeAwards} />}
      />
      <StatCard
        label="Last Transaction"
        value={<DateDisplay dateStr={stats.lastTransactionDate} />}
      />
    </section>
  )
}

function RecipientStatsSkeleton() {
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-xl border border-primary-200 bg-white p-5 shadow-sm dark:border-primary-700 dark:bg-primary-900"
        >
          <Skeleton className="mb-2 h-4 w-24" />
          <Skeleton className="h-8 w-32" />
        </div>
      ))}
    </section>
  )
}

// =============================================================================
// Analytics Section (Top Agencies + PSC Charts)
// =============================================================================

interface RecipientAnalyticsSectionProps {
  id: string
}

export function RecipientAnalyticsSection({
  id,
}: RecipientAnalyticsSectionProps) {
  return (
    <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <QueryErrorBoundary title="Failed to load agencies">
        <Suspense
          fallback={<AnalyticsChartSkeleton title="Top 5 Awarding Agencies" />}
        >
          <TopAgenciesChart id={id} />
        </Suspense>
      </QueryErrorBoundary>
      <QueryErrorBoundary title="Failed to load PSC data">
        <Suspense
          fallback={
            <AnalyticsChartSkeleton title="Top 5 Product Service Codes" />
          }
        >
          <PscChart id={id} />
        </Suspense>
      </QueryErrorBoundary>
    </section>
  )
}

function TopAgenciesChart({ id }: { id: string }) {
  const { data: dashboard } = useSuspenseRecipientDashboard(id)

  if (dashboard.topAgencies === null) {
    return <DeferredTopAgenciesChart id={id} />
  }

  return (
    <div className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
      <DistributionBar
        items={dashboard.topAgencies}
        title="Top 5 Awarding Agencies"
        showAmount
      />
    </div>
  )
}

function DeferredTopAgenciesChart({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const { data: agencies, isLoading } = useQuery(queries.analyticsAgencies(id))

  if (isLoading) {
    return <AnalyticsChartSkeleton title="Top 5 Awarding Agencies" />
  }
  if (!agencies) return null

  return (
    <div className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
      <DistributionBar
        items={agencies}
        title="Top 5 Awarding Agencies"
        showAmount
      />
    </div>
  )
}

function PscChart({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const { data: pscCodes } = useSuspenseQuery(queries.analyticsPsc(id))

  return (
    <div className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
      <DistributionBar
        items={pscCodes}
        title="Top 5 Product Service Codes"
        showAmount
      />
    </div>
  )
}

function AnalyticsChartSkeleton({ title }: { title: string }) {
  return (
    <div className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
      <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
        {title}
      </h3>
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="space-y-1">
            <div className="flex justify-between text-sm">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// Recent Awards Section
// =============================================================================

interface RecipientRecentAwardsSectionProps {
  id: string
}

export function RecipientRecentAwardsSection({
  id,
}: RecipientRecentAwardsSectionProps) {
  return (
    <QueryErrorBoundary title="Failed to load awards">
      <Suspense fallback={<RecentAwardsSkeleton />}>
        <RecentAwardsContent id={id} />
      </Suspense>
    </QueryErrorBoundary>
  )
}

function RecentAwardsContent({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const { data: awards } = useSuspenseQuery(queries.recentAwards(id))

  if (awards.length === 0) {
    return (
      <section className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
        <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
          Recent Prime Awards
        </h3>
        <p className="text-sm text-primary-500 dark:text-primary-400">
          No recent awards found for this recipient.
        </p>
      </section>
    )
  }

  return (
    <section className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
      <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
        Recent Prime Awards
      </h3>
      <div className="overflow-hidden rounded-lg border border-primary-200 dark:border-primary-700">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-primary-200 bg-primary-50 text-xs font-semibold uppercase text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
            <tr>
              <th className="px-4 py-3">Award ID</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Agency</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-primary-100 dark:divide-primary-800">
            {awards.map((award) => (
              <tr
                key={award.id}
                className="bg-white transition-colors hover:bg-primary-50 dark:bg-primary-900 dark:hover:bg-primary-800"
              >
                <td className="px-4 py-3">
                  <Link
                    href={`/awards/usaspending/${award.id}`}
                    className="font-mono font-medium text-accent-500 hover:underline dark:text-accent-400"
                  >
                    {award.piid}
                  </Link>
                </td>
                <td className="px-4 py-3 text-primary-600 dark:text-primary-400">
                  <DateDisplay dateStr={award.date} />
                </td>
                <td className="px-4 py-3 text-primary-600 dark:text-primary-400">
                  {award.agency}
                </td>
                <td className="px-4 py-3">
                  <span className="rounded border border-primary-200 bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300">
                    {award.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-medium tabular-nums text-primary-900 dark:text-primary-100">
                  <Currency value={award.amount} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function RecentAwardsSkeleton() {
  return (
    <section className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
      <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
        Recent Prime Awards
      </h3>
      <div className="overflow-hidden rounded-lg border border-primary-200 dark:border-primary-700">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-primary-200 bg-primary-50 text-xs font-semibold uppercase text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
            <tr>
              <th className="px-4 py-3">Award ID</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Agency</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-primary-100 dark:divide-primary-800">
            {[1, 2, 3].map((i) => (
              <tr key={i} className="bg-white dark:bg-primary-900">
                <td className="px-4 py-3">
                  <Skeleton className="h-5 w-24" />
                </td>
                <td className="px-4 py-3">
                  <Skeleton className="h-5 w-24" />
                </td>
                <td className="px-4 py-3">
                  <Skeleton className="h-5 w-36" />
                </td>
                <td className="px-4 py-3">
                  <Skeleton className="h-5 w-20" />
                </td>
                <td className="px-4 py-3 text-right">
                  <Skeleton className="ml-auto h-5 w-24" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// =============================================================================
// Shared Components
// =============================================================================

interface StatCardProps {
  label: string
  value: React.ReactNode
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div className="rounded-xl border border-primary-200 bg-white p-5 shadow-sm dark:border-primary-700 dark:bg-primary-900">
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400">
        {label}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-primary-900 dark:text-primary-100">
        {value}
      </div>
    </div>
  )
}
